import { useTranslator } from 'tradera/lang/translate';
import React from 'react';
import { Button, type ButtonProps } from '../atoms/button/button';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { startAuthOnFile } from 'tradera/components/bank-id/state/bank-id/actions';
import type {
    BankIdAuthenticationCancelApiRequest,
    BankIdAuthenticationCancelApiResponse
} from 'tradera/state/services/types/webapi-auth-generated';
import { webApiClient } from 'tradera/utils/http';

type GetBankIdLinkProps = {
    autoStartToken?: string | null;
    isIOS: boolean;
};

const getBankIdLink = ({ autoStartToken, isIOS }: GetBankIdLinkProps) => {
    const queryParams = new URLSearchParams();
    if (autoStartToken) {
        queryParams.set('autostarttoken', autoStartToken);
    }
    if (isIOS) {
        queryParams.set('redirect', 'tradera-native://bank_id');
    } else {
        queryParams.set('redirect', 'null');
    }

    return `bankid:///?${queryParams}`;
};

type OpenOnDeviceButtonProps = ButtonProps & {
    autoStartToken?: string | null;
};

const OpenOnDeviceButton = ({
    autoStartToken,
    ...props
}: OpenOnDeviceButtonProps) => {
    const { t } = useTranslator();

    const isMobile = useIsMobile();
    const dispatch = useAppDispatch();

    const { bankIdOrderRef } = useAppSelector((state) => state.bankId);
    const { isIOS } = useAppSelector((state) => state.environment);

    const bankIdStartUrl = getBankIdLink({
        autoStartToken,
        isIOS: isIOS ?? false
    });

    const startOnFileAuth = async () => {
        await webApiClient.post<
            BankIdAuthenticationCancelApiResponse,
            BankIdAuthenticationCancelApiRequest
        >('/auth/bankid/cancel', { bankIdOrderRef });

        const autoStartToken = await dispatch(startAuthOnFile());
        if (!autoStartToken) return;

        window.location.href = getBankIdLink({
            autoStartToken,
            isIOS: false
        });
    };

    return (
        <Button
            onClick={isMobile ? undefined : startOnFileAuth}
            href={isMobile ? bankIdStartUrl : undefined}
            rel="noopener noreferrer"
            theme="primary"
            role="button"
            loading={!autoStartToken}
            {...props}>
            {t('bankIdModal_OpenAppOnThisDevice')}
        </Button>
    );
};

export default OpenOnDeviceButton;
